import chroma from 'chroma-js';

export const formatHexStringToRGBAArray = (color: string) => {
  const rgba = chroma(color).rgba();
  return rgba; // [...rgba.slice(0, 3).map(x => x / 255), rgba[3]];
};

const FIDELITY = 100;
const ACCESSIBLE_RATIO = 4.5; // 4.5 is WCAG standard

const effectiveAlphaColor: (foregroundColor: string, backgroundColor?: string) => string = (
  foregroundColor: string,
  backgroundColor: string = '#FFFFFF'
) => {
  var fgRgba = chroma(foregroundColor).rgba();
  var alpha = fgRgba[3];
  var bgRgb =
    chroma(backgroundColor).name() === 'white'
      ? chroma(backgroundColor).rgb()
      : chroma(effectiveAlphaColor(backgroundColor, '#FFF')).rgb();

  return chroma(
    (1 - alpha) * bgRgb[0] + alpha * fgRgba[0],
    (1 - alpha) * bgRgb[1] + alpha * fgRgba[1],
    (1 - alpha) * bgRgb[2] + alpha * fgRgba[2]
  ).hex();
};

export const bestContrast = (backgroundColor: string, colorA: string, colorB: string) => {
  return chroma.contrast(effectiveAlphaColor(backgroundColor), effectiveAlphaColor(colorA)) >
    chroma.contrast(effectiveAlphaColor(backgroundColor), effectiveAlphaColor(colorB))
    ? colorA
    : colorB;
};

export const closestWCAGGrey = (
  backgroundColor: string,
  contrastRatio: number = ACCESSIBLE_RATIO,
  fidelity: number = FIDELITY,
  nearestColor?: string,
  light?: boolean
) => {
  // make sure background color isn't an alpha'd color
  backgroundColor = effectiveAlphaColor(backgroundColor);

  // create a grey scale for acceptable colors
  const greyscale = chroma.scale();
  const greys = Array.apply(null, Array(fidelity)).map((ele: any, index: number) =>
    greyscale(index / fidelity).hex()
  ) as string[];

  // extract acceptable greys based on contrastRatio threshold
  const accessibleGreys = greys
    .map(grey => ({
      color: grey,
      contrast: chroma.contrast(backgroundColor, grey)
    }))
    .filter(greyContrObj => greyContrObj.contrast > contrastRatio);

  // find the most middle grey color of acceptable matches
  let match;
  if (nearestColor && light !== undefined) {
    // find color that is most middle but closer in kind to nearest color
    const matches = accessibleGreys.filter(grey =>
      light
        ? parseInt(grey.color.substring(1), 16) > parseInt(nearestColor.substring(1), 16)
        : parseInt(grey.color.substring(1), 16) < parseInt(nearestColor.substring(1), 16)
    );
    // light-middle will be the lowest match, dark-middle will be the highest match
    // greyscale goes from dark to light
    match = light ? matches.slice(-1)[0] : matches[0];
  } else {
    match =
      accessibleGreys.filter((grey, i, array) => {
        const min = Math.min.apply(Math, array.map(e => e.contrast));
        return grey.contrast === min;
      })[0] || null;
  }
  return match && match.color;
};

export const closestWCAAColorContrast = (
  backgroundColor: string,
  contrastRatio: number = ACCESSIBLE_RATIO,
  fidelity: number = FIDELITY,
  colorSteps: string[],
  nearestColor?: string,
  light?: boolean
) => {
  backgroundColor = chroma(backgroundColor).hex();
  nearestColor = nearestColor && chroma(nearestColor).hex();
  colorSteps = colorSteps.map(step => chroma(step).hex());

  // make sure background color isn't an alpha'd color
  backgroundColor = effectiveAlphaColor(backgroundColor);

  // escape hatch for when nearest color is accessible already
  if (nearestColor && chroma.contrast(backgroundColor, nearestColor) > contrastRatio) {
    return nearestColor;
  }

  // create a grey scale for acceptable colors
  const colorscale = chroma.scale(colorSteps);
  const colors = Array.apply(null, Array(fidelity)).map((ele: any, index: number) =>
    colorscale(index / fidelity).hex()
  ) as string[];

  // extract acceptable colors based on contrastRatio threshold
  const accessibleColors = colors
    .map(color => ({
      color: color,
      contrast: chroma.contrast(backgroundColor, color)
    }))
    .filter(colorContrObj => colorContrObj.contrast > contrastRatio);

  // get a default for light
  if (light === undefined) {
    if (colorSteps.length > 1) {
      // take the first and last step as min/max
      light = colorSteps[0] !== bestContrast(backgroundColor, colorSteps[0], colorSteps[colorSteps.length - 1]);
    } else {
      // use white and black as default
      light = '#000' !== bestContrast(backgroundColor, '#000', '#fff');
    }
  }

  // find the most middle color color of acceptable matches
  let match;
  if (nearestColor && nearestColor.substring(1) && light !== undefined) {
    // find color that is most middle but closer in kind to nearest color
    const matches = accessibleColors.filter(color =>
      light
        ? parseInt(color.color.substring(1), 16) > parseInt(nearestColor!.substring(1), 16)
        : parseInt(color.color.substring(1), 16) < parseInt(nearestColor!.substring(1), 16)
    );
    // light-middle will be the lowest match, dark-middle will be the highest match
    // colorscale goes from dark to light

    match = !light ? matches.slice(-1)[0] : matches[0];
  } else {
    match =
      accessibleColors.filter((color, i, array) => {
        const min = Math.min.apply(Math, array.map(e => e.contrast));
        return color.contrast === min;
      })[0] || null;
  }
  return match && match.color;
};

export const activeInnactiveRecipe = (backgroundColor: string, fgColorLight: string, fgColorDark: string) => {
  return {
    active: bestContrast(backgroundColor, fgColorDark, fgColorLight),
    inactive: closestWCAGGrey(
      backgroundColor,
      1.5,
      80,
      chroma.average([fgColorDark, fgColorLight]).hex() as any,
      bestContrast(backgroundColor, fgColorDark, fgColorLight) === fgColorLight
    )
  };
};

export const bestContrastedColorRecipe = (backgroundColor: string, color: string) => {
  return closestWCAAColorContrast(backgroundColor, 2.2, 100, ['#000', color, '#FFF'], color);
};
